import {
  DiscountEntry,
  EDiscountEntryType,
  describeLineDiscountEquivalent,
  formatDiscountEntry,
} from 'utilities/discount-entry';

export type PosCartItemDiscount = {
  unitPrice: number;
  quantity: number;
  discount: number;
  isDiscountInPercentage: boolean;
};

// compute per-item discount amount based on whether it's percentage or flat discount
export const getItemDiscountAmount = (item: PosCartItemDiscount): number => {
  const lineTotal = item.unitPrice * item.quantity;
  if (item.isDiscountInPercentage) {
    return Number(((lineTotal * item.discount) / 100).toFixed(2));
  }
  // flat discount — cap it at the line total
  return Number(Math.min(item.discount, lineTotal).toFixed(2));
};

// The POS line discount as entered: a percent of the line total, or a flat taka amount on the
// line (TK 50 on quantity 2 stays 50). A flat amount is stored as applied, capped at the line total.
export const getItemDiscountEntry = (item: PosCartItemDiscount): DiscountEntry => {
  if (!item.discount) return { discountEntryValue: null, discountEntryType: null };
  if (item.isDiscountInPercentage) {
    return { discountEntryValue: item.discount, discountEntryType: EDiscountEntryType.Percent };
  }
  return {
    discountEntryValue: getItemDiscountAmount(item),
    discountEntryType: EDiscountEntryType.Amount,
  };
};

export const formatItemDiscountEntry = (item: PosCartItemDiscount): string => {
  const { discountEntryValue, discountEntryType } = getItemDiscountEntry(item);
  return formatDiscountEntry(discountEntryValue, discountEntryType, getItemDiscountAmount(item));
};

// The small line under a POS discount: "7.58% · TK 25.00 each" for TK 50 on 2 units.
export const describeItemDiscountEquivalent = (item: PosCartItemDiscount): string => {
  const { discountEntryValue, discountEntryType } = getItemDiscountEntry(item);
  return describeLineDiscountEquivalent(
    item.quantity,
    item.unitPrice,
    getItemDiscountAmount(item),
    discountEntryValue,
    discountEntryType,
  );
};

// compute per-item net total (after discount)
export const getItemNetTotal = (item: PosCartItemDiscount): number => {
  return item.unitPrice * item.quantity - getItemDiscountAmount(item);
};

// The sale-level discountCost sent to the API. The API already subtracts each line's discountCost
// from the subtotal, so this is the order discount only; adding line discounts here would
// subtract them twice and save a total below what the customer paid.
export const getPosSaleDiscountCost = (orderDiscountAmount: number): number =>
  Number(orderDiscountAmount.toFixed(2));
